import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { practitionerService } from '../../services/practitionerService';
import { TrainingAssignment, TrainingModule, ContentType } from '../../types/training';
import { useContentProtection } from '../../hooks/useContentProtection';
import { ProtectedPDFViewer } from '../../components/ProtectedPDFViewer';

export const ContentViewer: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<TrainingAssignment | null>(null);
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingViewed, setMarkingViewed] = useState(false);

  // Enable content protection for training materials
  const { protectionStyles, printProtectionCSS } = useContentProtection({
    disablePrint: true,
    disableRightClick: true,
    disableTextSelection: true,
    disableCopy: true,
    disableScreenshot: true,
  });

  const loadContent = useCallback(async () => {
    if (!assignmentId) return;

    try {
      setLoading(true);
      setError(null);
      const assignmentData = await practitionerService.getAssignment(assignmentId);
      setAssignment(assignmentData);

      const moduleData = await practitionerService.getModule(assignmentData.moduleId);
      setModule(moduleData);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load content');
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (module && module.contentFiles.length > 0) {
      loadContentUrl(module.contentFiles[currentFileIndex].id);
    }
  }, [currentFileIndex, module]);

  const loadContentUrl = async (fileId: string) => {
    try {
      const response = await practitionerService.getContentUrl(fileId);
      setContentUrl(response.url);
    } catch (err: any) {
      console.error('Error loading content URL:', err);
      setError('Failed to load content URL');
    }
  };

  const handleMarkAsViewed = async () => {
    if (!assignmentId || !assignment || assignment.progress.contentViewed) return;

    try {
      setMarkingViewed(true);
      await practitionerService.markContentViewed(assignmentId);
      setAssignment({
        ...assignment,
        progress: {
          ...assignment.progress,
          contentViewed: true,
        },
      });
    } catch (err: any) {
      console.error('Error marking content as viewed:', err);
    } finally {
      setMarkingViewed(false);
    }
  };

  const handleNext = () => {
    if (module && currentFileIndex < module.contentFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
  };

  const handleFinish = async () => {
    await handleMarkAsViewed();
    navigate(`/training/${assignmentId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !assignment || !module) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error || 'Content not found'}</p>
        <button
          onClick={() => navigate(`/training/${assignmentId}`)}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Training
        </button>
      </div>
    );
  }

  if (module.contentFiles.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No content files available</p>
        <button
          onClick={() => navigate(`/training/${assignmentId}`)}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Training
        </button>
      </div>
    );
  }

  const currentFile = module.contentFiles[currentFileIndex];
  const isLastFile = currentFileIndex === module.contentFiles.length - 1;

  return (
    <div className="min-h-screen bg-gray-900" style={protectionStyles}>
      {/* Header */}
      <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center print:hidden">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/training/${assignmentId}`)}
            className="text-gray-300 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div>
            <h2 className="text-lg font-medium">{module.title}</h2>
            <p className="text-sm text-gray-400">
              File {currentFileIndex + 1} of {module.contentFiles.length}: {currentFile.fileName}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!assignment.progress.contentViewed && (
            <button
              onClick={handleMarkAsViewed}
              disabled={markingViewed}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-600"
            >
              {markingViewed ? 'Marking...' : 'Mark as Viewed'}
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 120px)' }}>
        {contentUrl ? (
          <div className="w-full h-full bg-white">
            {currentFile.fileType === ContentType.VIDEO ? (
              <video
                src={contentUrl}
                controls
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                className="w-full h-full"
                onContextMenu={(e) => e.preventDefault()}
                style={protectionStyles}
              >
                Your browser does not support the video tag.
              </video>
            ) : currentFile.fileType === ContentType.PDF ? (
              <ProtectedPDFViewer 
                key={contentUrl} // Force re-render when URL changes
                url={contentUrl} 
                protectionStyles={protectionStyles} 
              />
            ) : (
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(contentUrl)}`}
                className="w-full h-full"
                title={currentFile.fileName}
                style={protectionStyles}
              />
            )}
          </div>
        ) : (
          <div className="text-white">Loading content...</div>
        )}
      </div>

      {/* Navigation Footer */}
      <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center print:hidden">
        <button
          onClick={handlePrevious}
          disabled={currentFileIndex === 0}
          className="px-4 py-2 bg-gray-700 rounded hover:bg-gray-600 disabled:bg-gray-900 disabled:text-gray-600"
        >
          Previous
        </button>
        <div className="text-sm text-gray-400">
          {currentFileIndex + 1} / {module.contentFiles.length}
        </div>
        {isLastFile ? (
          <button
            onClick={handleFinish}
            className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
          >
            Finish & Return
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="px-4 py-2 bg-gray-700 rounded hover:bg-gray-600"
          >
            Next
          </button>
        )}
      </div>

      {/* CSS to disable printing and add watermark */}
      <style>{printProtectionCSS}</style>
    </div>
  );
};
